<script setup lang="ts">
import { Camera } from '@lucide/vue'
import { memberEmail, memberMailDomain } from '@zerohuman/shared'
import {
    Button,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    TagsInput,
    TagsInputItem,
    TagsInputItemDelete,
    TagsInputItemText,
    Textarea,
    ZhFieldError,
    ZhFormField,
    ZhFormLayout,
    ZhSavePreview,
} from '@zerohuman/ui'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import { api, type Me, type Role, type Team } from '@/api'
import MemberAvatar from '@/components/MemberAvatar.vue'
import { useFieldValidation } from '@/composables/useFieldValidation'
import { useFormDraft } from '@/composables/useFormDraft'
import { useFormFunnel } from '@/composables/useFormFunnel'
import { avatarDataUrl } from '@/lib/avatar'
import { maxLength, requiredName } from '@/lib/fieldRules'
import { buildMemberPreview } from '@/lib/formPreview'
import { NO_ROLE_ON_TEAM, pickedId, teamRoleOf } from '@/lib/memberPickers'

/**
 * Every control here is the kit's (#1223): name and email are the kit `Input`, the persona the kit `Textarea`, each
 * under a kit `Label`, and the three pickers the kit `Select`, on kit surfaces. The screen states no colour, radius,
 * border or fill of its own. A Select's `aria-label` and `data-testid` land on the field it mirrors its choice on;
 * a visually hidden span names each trigger.
 */
const router = useRouter()
/** The form's funnel events (#1774), under the `teammate` flow. */
const funnel = useFormFunnel('teammate')
const name = ref('')
const email = ref('')
/** Set once the email field is typed in: from then on the name stops filling it. */
const emailEdited = ref(false)
const persona = ref('')
const error = ref<string | null>(null)
const saving = ref(false)
/** Name is required and Persona holds 500 characters (the fields' own rules), checked while typing (#1757). */
const fields = useFieldValidation(
    computed(() => ({ name: name.value, persona: persona.value })),
    { name: requiredName('member'), persona: maxLength('Persona', 500) },
    { idPrefix: 'create-member' },
)

const avatar = ref<string | null>(null)
const avatarInput = ref<HTMLInputElement | null>(null)
const me = ref<Me | null>(null)
const roles = ref<Role[]>([])
const teams = ref<Team[]>([])
/** A team can be picked on its own; a role alongside it is a shortcut, not what makes the membership. */
const teamPicks = ref<Array<{ teamId: string; roleId: string | null }>>([])
const roleIds = ref<string[]>([])
const pickTeam = ref('')
/** The kit Select cannot hold an empty value, so "no role on that team" is a value of its own. */
const pickTeamRole = ref(NO_ROLE_ON_TEAM)
const pickRole = ref('')
/**
 * The email as the draft keeps it: only an address the person typed. One the name filled in is the form's own, made
 * again from the restored name once `me` loads, so merely opening the form keeps nothing.
 */
const typedEmail = computed({
    get: () => (emailEdited.value ? email.value : ''),
    set: (saved: string) => {
        if (saved) email.value = saved
    },
})
/**
 * #1763: what is typed or picked here is kept in this browser until the member is created, so leaving (Cancel, another
 * link, a reload, a closed tab) loses nothing. Nothing of it is sent until Create member is confirmed. `emailEdited`
 * goes with the email: an address the person typed stays theirs, and the name does not fill it again once `me` loads.
 */
const draft = useFormDraft('create-member', {
    name,
    email: typedEmail,
    emailEdited,
    persona,
    avatar,
    teamPicks,
    roleIds,
})
// A restored draft is not a change made on this page: its fields show no error until changed or left.
fields.reset()

const preview = computed(() => ({ name: name.value, avatarUrl: avatar.value }))
/** `{slug}.zerohuman.com` for the signed-in enterprise, or empty until `me` has loaded (or if it never does). */
const mailDomain = computed(() => (me.value ? memberMailDomain(me.value.enterprise.slug) : ''))
/** The email field before a name has been typed: just the domain, so the person sees where the address will live. */
const bareEmail = computed(() => (mailDomain.value ? `@${mailDomain.value}` : ''))
const availableTeams = computed(() => {
    const picked = new Set(teamPicks.value.map(pick => pick.teamId))
    return teams.value.filter(team => !picked.has(team.id))
})
const availableRoles = computed(() => {
    const held = new Set(roleIds.value)
    return roles.value.filter(role => !held.has(role.id))
})

onMounted(async () => {
    const [allRoles, allTeams, session] = await Promise.all([
        api.roles().catch(() => null),
        api.teams().catch(() => null),
        api.me().catch(() => null),
    ])
    roles.value = allRoles ?? []
    teams.value = allTeams ?? []
    me.value = session
    dropPicksNoLongerHeld(allRoles, allTeams)
    fillEmail()
})

/**
 * #1763: a restored team or role the company no longer holds could not be shown by name, and joining or assigning it
 * would fail after the member was made, with the draft already gone: it is dropped once the lists load. A team pick
 * whose role there is gone keeps the team, with no role on it. A list that could not be read drops nothing.
 */
function dropPicksNoLongerHeld(heldRoles: Role[] | null, heldTeams: Team[] | null): void {
    const roleHeld = (roleId: string): boolean => !heldRoles || heldRoles.some(role => role.id === roleId)
    const teamHeld = (teamId: string): boolean => !heldTeams || heldTeams.some(team => team.id === teamId)
    if (teamPicks.value.some(pick => !teamHeld(pick.teamId) || (pick.roleId && !roleHeld(pick.roleId)))) {
        teamPicks.value = teamPicks.value
            .filter(pick => teamHeld(pick.teamId))
            .map(pick => (pick.roleId && !roleHeld(pick.roleId) ? { ...pick, roleId: null } : pick))
    }
    const kept = roleIds.value.filter(roleHeld)
    if (kept.length !== roleIds.value.length) roleIds.value = kept
}

/** Typing an email takes it over from the name; clearing the field hands it back. */
function onEmailInput(event: Event): void {
    emailEdited.value = (event.target as HTMLInputElement).value.length > 0
}

/**
 * Derive the email from the name at the enterprise's own domain, unless the person has typed their own.
 * Runs when the name loses focus and again on submit, so pressing Enter in the name field still derives it.
 */
function fillEmail(): void {
    if (emailEdited.value || !mailDomain.value) {
        return
    }
    email.value = name.value.trim() ? memberEmail(name.value, mailDomain.value) : bareEmail.value
}

function roleName(roleId: unknown): string {
    return roles.value.find(role => role.id === roleId)?.name ?? String(roleId)
}

function teamLabel(teamId: unknown): string {
    const teamName = teams.value.find(row => row.id === teamId)?.name ?? String(teamId)
    const roleId = teamPicks.value.find(row => row.teamId === teamId)?.roleId
    return roleId ? `${teamName} · ${roleName(roleId)}` : teamName
}

async function onAvatarPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement
    const file = input.files?.[0]
    input.value = ''
    if (!file) {
        return
    }
    try {
        avatar.value = await avatarDataUrl(file)
        error.value = null
    } catch (caught) {
        error.value =
            caught instanceof Error ? `Could not use that photo (${caught.message}).` : 'Could not use that photo.'
    }
}

/** A team picked through the kit Select; picking another team starts its role over, as the native one did. */
function chooseTeam(choice: unknown): void {
    pickTeam.value = pickedId(choice)
    pickTeamRole.value = NO_ROLE_ON_TEAM
}

function chooseTeamRole(choice: unknown): void {
    pickTeamRole.value = pickedId(choice) || NO_ROLE_ON_TEAM
}

function addTeam(): void {
    const teamId = pickTeam.value
    if (!teamId) {
        return
    }
    teamPicks.value = [...teamPicks.value, { teamId, roleId: teamRoleOf(pickTeamRole.value) }]
    pickTeam.value = ''
    pickTeamRole.value = NO_ROLE_ON_TEAM
}

function removeTeam(teamId: unknown): void {
    teamPicks.value = teamPicks.value.filter(pick => pick.teamId !== String(teamId))
}

/** A role picked through the kit Select is assigned at once, and the picker goes back to empty. */
function addRole(choice: unknown): void {
    const roleId = pickedId(choice)
    pickRole.value = ''
    if (roleId && !roleIds.value.includes(roleId)) {
        roleIds.value = [...roleIds.value, roleId]
    }
}

function removeRole(roleId: unknown): void {
    roleIds.value = roleIds.value.filter(id => id !== String(roleId))
}

/**
 * The save preview (#1756): pressing Create member says in plain words who will be added, to which teams and in which
 * roles, and nothing is created until that is confirmed. Any change to the form hides it as stale.
 */
const savePreview = ref<string[] | null>(null)

function openPreview(): void {
    // Validate first (CTO decision 19 on #1756): an invalid field shows its error, takes focus and opens no preview.
    if (fields.check() !== null) return
    // Derived first, so focus leaving the name for the preview changes nothing the preview was built from.
    fillEmail()
    const teamNames = teamPicks.value.map(pick => teams.value.find(team => team.id === pick.teamId)?.name ?? '')
    const roleIdsHeld = [
        ...teamPicks.value.map(pick => pick.roleId).filter((roleId): roleId is string => Boolean(roleId)),
        ...roleIds.value,
    ]
    const roleTitles = [...new Set(roleIdsHeld)].map(roleId => roles.value.find(role => role.id === roleId)?.name ?? '')
    savePreview.value = buildMemberPreview({ name: name.value, teamNames, roleTitles })
}

/**
 * Synchronous, so it runs inside the change that triggers it. `openPreview` derives the email before it builds the
 * preview; a queued watcher would only run after that and hide the preview it just opened (pressing Enter in the
 * name field, with no blur, showed nothing and sent nothing). Only a change made after the preview opens hides it.
 */
watch(
    [name, email, persona, avatar, teamPicks, roleIds],
    () => {
        savePreview.value = null
    },
    { deep: true, flush: 'sync' },
)

async function confirmPreview(): Promise<void> {
    savePreview.value = null
    await create()
}

async function create(): Promise<void> {
    if (fields.check() !== null) return
    saving.value = true
    error.value = null
    let memberId: string | null = null
    fillEmail()
    try {
        const member = await api.createMember({
            name: name.value,
            // The bare domain is the prefill, not an address: leave the API to derive one from the name.
            email: email.value && email.value !== bareEmail.value ? email.value : undefined,
            persona: persona.value || undefined,
        })
        memberId = member.id
        // Saved once the member exists, whatever a later photo, team or role call does: the view opens it either way.
        // So the draft goes too, and the next visit starts blank rather than adding the same person twice.
        draft.clear()
        funnel.completed()
        if (avatar.value) {
            await api.setMemberAvatar(member.id, avatar.value)
        }
        const joined = new Set<string>()
        for (const pick of teamPicks.value) {
            await api.addTeamMember(pick.teamId, member.id, pick.roleId)
            if (pick.roleId) {
                joined.add(pick.roleId)
            }
        }
        // Joining a team already assigned that team's role, so only the rest are left to assign.
        for (const roleId of roleIds.value) {
            if (!joined.has(roleId)) {
                await api.assignRole(member.id, roleId)
            }
        }
        await router.push(`/members/${member.id}`)
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not create (${caught.message}).` : 'Could not create.'
        // The member exists from here on, so resubmitting would only collide: open what was made.
        if (memberId) {
            await router.push(`/members/${memberId}`)
        }
    } finally {
        saving.value = false
    }
}
</script>

<template>
	<section class="w-full space-y-6 p-8">
		<p class="text-muted-foreground text-sm">
			<RouterLink class="underline" to="/members">Members</RouterLink>
		</p>
		<!-- #1756: the shared form shell, one help line on every field, and a plain-words preview before saving. -->
		<ZhFormLayout
			title="Create member"
			purpose="Adds a person to the company, with the teams and roles they will work in."
			data-testid="create-form"
			@submit.prevent="openPreview"
		>
			<template #status>
				<p v-if="error" class="text-destructive text-sm">{{ error }}</p>
			</template>
			<ZhFormField label="Photo" help="Optional; their initials stand in until you pick one." example="a square headshot" group>
			<div class="flex items-center gap-5">
				<div class="relative shrink-0">
					<MemberAvatar :member="preview" class="size-24" fallback-class="text-3xl" />
					<Button
						type="button"
						size="icon-sm"
						variant="outline"
						class="absolute right-0 bottom-0"
						aria-label="Upload photo"
						data-testid="pick-avatar"
						:disabled="saving"
						@click="avatarInput?.click()"
					>
						<Camera />
					</Button>
					<input
						ref="avatarInput"
						type="file"
						accept="image/png,image/jpeg,image/webp,image/gif"
						class="hidden"
						data-testid="avatar-file"
						@change="onAvatarPicked"
					/>
				</div>
			</div>
			</ZhFormField>
				<!-- #1757's checks (CTO decisions 18 and 20 on #1756): a fixed id, described by the error, then the help line. -->
				<ZhFormField :id="fields.fieldId('name')" :error-id="fields.message('name').id" label="Name" help="Their full name, as the team will see it." example="Ada Lovelace">
					<template #default="{ controlId, describedBy }">
						<Input v-model="name" required v-bind="fields.bind('name')" :id="controlId" :aria-describedby="describedBy" @blur="fillEmail" />
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('name')" />
					</template>
				</ZhFormField>
				<ZhFormField v-slot="{ controlId, helpId }" label="Email" help="Their work address, made from the name if you leave it." example="ada.lovelace@acme.zerohuman.com">
					<Input
						:id="controlId"
						v-model="email"
						:aria-describedby="helpId"
						type="text"
						inputmode="email"
						autocomplete="off"
						spellcheck="false"
						placeholder="from the name if empty"
						data-testid="member-email"
						@input="onEmailInput"
					/>
				</ZhFormField>
				<ZhFormField :id="fields.fieldId('persona')" :error-id="fields.message('persona').id" label="Persona" help="How they work and sound, in a sentence or two." example="Careful, exact and quick to reply">
					<template #default="{ controlId, describedBy }">
						<Textarea v-model="persona" rows="3" maxlength="500" v-bind="fields.bind('persona')" :id="controlId" :aria-describedby="describedBy" />
					</template>
					<template #error>
						<ZhFieldError v-bind="fields.message('persona')" />
					</template>
				</ZhFormField>
			<ZhFormField label="Teams" help="Teams they join, with an optional role; a team's work goes to holders who sit on it." example="Sales, as Account Executive" group data-testid="create-member-teams">
				<div class="space-y-2">
				<TagsInput
					:model-value="teamPicks.map(pick => pick.teamId)"
					:display-value="teamLabel"
					:disabled="saving"
					class="min-h-9"
					@remove-tag="removeTeam"
				>
					<TagsInputItem v-for="pick in teamPicks" :key="pick.teamId" :value="pick.teamId">
						<TagsInputItemText />
						<TagsInputItemDelete tabindex="0" :aria-label="`Remove from ${teamLabel(pick.teamId)}`" />
					</TagsInputItem>
					<span v-if="!teamPicks.length" class="text-muted-foreground px-1">Not on a team.</span>
				</TagsInput>
				<div v-if="availableTeams.length" class="flex flex-wrap items-center gap-2">
					<span id="create-member-add-team" class="sr-only">Add to team</span>
					<Select
						:model-value="pickTeam"
						:disabled="saving"
						aria-label="Add to team"
						@update:model-value="chooseTeam"
					>
						<SelectTrigger size="sm" aria-labelledby="create-member-add-team">
							<SelectValue placeholder="Add to team…" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem v-for="team in availableTeams" :key="team.id" :value="team.id">
								{{ team.name }}
							</SelectItem>
						</SelectContent>
					</Select>
					<template v-if="pickTeam && roles.length">
						<span id="create-member-team-role" class="sr-only">Role to hold</span>
						<Select
							:model-value="pickTeamRole"
							:disabled="saving"
							aria-label="Role to hold"
							@update:model-value="chooseTeamRole"
						>
							<SelectTrigger size="sm" aria-labelledby="create-member-team-role">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem :value="NO_ROLE_ON_TEAM">As role… (optional)</SelectItem>
								<SelectItem v-for="role in roles" :key="role.id" :value="role.id">{{ role.name }}</SelectItem>
							</SelectContent>
						</Select>
					</template>
					<Button type="button" size="sm" :disabled="saving || !pickTeam" @click="addTeam">Add</Button>
				</div>
				</div>
			</ZhFormField>
			<ZhFormField label="Roles" help="Jobs they hold across the company, whatever their team." example="Account Executive" group data-testid="create-member-roles">
				<TagsInput
					:model-value="roleIds"
					:display-value="roleName"
					:disabled="saving"
					class="min-h-9"
					@remove-tag="removeRole"
				>
					<TagsInputItem v-for="roleId in roleIds" :key="roleId" :value="roleId">
						<TagsInputItemText />
						<TagsInputItemDelete tabindex="0" :aria-label="`Remove ${roleName(roleId)}`" />
					</TagsInputItem>
					<template v-if="availableRoles.length">
						<span id="create-member-assign-role" class="sr-only">Assign role</span>
						<Select
							:model-value="pickRole"
							:disabled="saving"
							aria-label="Assign role"
							data-testid="assign-role"
							@update:model-value="addRole"
						>
							<SelectTrigger size="sm" aria-labelledby="create-member-assign-role">
								<SelectValue placeholder="Assign role…" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem v-for="role in availableRoles" :key="role.id" :value="role.id">
									{{ role.name }}
								</SelectItem>
							</SelectContent>
						</Select>
					</template>
					<span v-else-if="!roleIds.length" class="text-muted-foreground px-1">No roles to assign.</span>
				</TagsInput>
			</ZhFormField>
			<template #cancel>
				<Button type="button" size="sm" variant="outline" @click="router.push('/members')">Cancel</Button>
			</template>
			<template #primary>
				<Button type="submit" size="sm" :disabled="saving">Create member</Button>
			</template>
			<template #preview>
				<ZhSavePreview v-if="savePreview" :lines="savePreview" confirm-label="Create member" :pending="saving" @confirm="confirmPreview" @edit="savePreview = null" />
			</template>
		</ZhFormLayout>
	</section>
</template>
